import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { gardenAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  TreeDeciduous,
  Droplets,
  Heart,
  Award,
  Sparkles,
  AlertTriangle,
  Trophy,
  Leaf,
  Sun,
  Moon,
  Zap,
  Shield,
  Eye,
  RefreshCw,
  ChevronRight,
  Lock,
  Users,
  Calendar,
  TrendingUp,
  Star,
  Flame,
  Crown
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 7 Visual Levels mapping backend stages to user-friendly names
const VISUAL_LEVELS = [
  { level: 1, name: 'Semente',           stages: ['seed', 'germination'],        days: 0,   icon: '🌱', color: 'bg-lime-500' },
  { level: 2, name: 'Broto',             stages: ['sprout'],                      days: 30,  icon: '🌿', color: 'bg-green-500' },
  { level: 3, name: 'Muda',              stages: ['young'],                       days: 60,  icon: '🌲', color: 'bg-emerald-500' },
  { level: 4, name: 'Árvore Jovem',      stages: ['structure'],                   days: 90,  icon: '🌳', color: 'bg-teal-500' },
  { level: 5, name: 'Árvore Madura',     stages: ['canopy', 'flowering'],         days: 180, icon: '🌸', color: 'bg-cyan-500' },
  { level: 6, name: 'Árvore com Frutos', stages: ['adult'],                       days: 365, icon: '🍎', color: 'bg-amber-500' },
  { level: 7, name: 'Árvore Sagrada',    stages: ['ancestral', 'guardian'],        days: 540, icon: '👑', color: 'bg-violet-500' },
];

function getCurrentLevel(stage) {
  if (!stage) return VISUAL_LEVELS[0];
  for (let i = VISUAL_LEVELS.length - 1; i >= 0; i--) {
    if (VISUAL_LEVELS[i].stages.includes(stage)) return VISUAL_LEVELS[i];
  }
  return VISUAL_LEVELS[0];
}

function LevelProgressionBar({ currentStage, daysActive }) {
  const current = getCurrentLevel(currentStage);
  const nextLevel = VISUAL_LEVELS.find(l => l.level === current.level + 1);
  const daysToNext = nextLevel ? Math.max(0, nextLevel.days - daysActive) : 0;

  return (
    <Card className="rounded-2xl border-border/40 shadow-sm overflow-hidden" data-testid="level-progression">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{current.icon}</span>
            <div>
              <p className="font-bold text-sm">Nível {current.level} — {current.name}</p>
              <p className="text-xs text-muted-foreground">{daysActive} dias de jornada</p>
            </div>
          </div>
          {nextLevel && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Próximo nível</p>
              <p className="text-sm font-semibold flex items-center gap-1">{nextLevel.icon} {nextLevel.name}</p>
              <p className="text-[10px] text-muted-foreground">~{daysToNext} dias restantes</p>
            </div>
          )}
          {!nextLevel && <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">Nível Máximo</Badge>}
        </div>

        {/* Level dots */}
        <div className="flex items-center gap-1">
          {VISUAL_LEVELS.map(l => {
            const isComplete = l.level < current.level;
            const isCurrent = l.level === current.level;
            return (
              <div key={l.level} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-full h-1.5 rounded-full transition-all ${isComplete ? l.color : isCurrent ? `${l.color} opacity-60` : 'bg-muted'}`} />
                <span className={`text-[9px] hidden sm:block ${isCurrent ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>{l.icon}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Free/Premium banner
function PlanBanner({ isPremium }) {
  if (isPremium) return null;
  return (
    <Card className="rounded-2xl border-violet-200 dark:border-violet-800/40 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20" data-testid="premium-banner">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="p-2 rounded-xl bg-violet-100 dark:bg-violet-900/40">
          <Crown className="h-5 w-5 text-violet-600 dark:text-violet-400" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-violet-900 dark:text-violet-200">Quer ter sua própria Árvore Viva NFT?</p>
          <p className="text-xs text-violet-600 dark:text-violet-400">No plano Premium, você recebe 3 sementes exclusivas que evoluem e podem ser alocadas na Carteira VSA.</p>
        </div>
        <Button size="sm" className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white flex-shrink-0" onClick={() => window.location.href='/planos'} data-testid="premium-upsell-btn">
          Conhecer
        </Button>
      </CardContent>
    </Card>
  );
}

// Componente de Árvore Visual
const ENANTIODROMIA_IMAGES = {
  seed: 'https://cdn.abacus.ai/images/5f0021b2-f7a9-4809-a011-544cb892676c.png',
  sprout: 'https://cdn.abacus.ai/images/1b0d7fe3-47fc-4a50-8e83-a80ff001d097.png',
  bloom: 'https://cdn.abacus.ai/images/99883393-e864-40c3-8adc-68edb7a6f640.png',
};

const getTreeImage = (stage) => {
  const seedStages = ['seed', 'germination'];
  const sproutStages = ['sprout', 'young', 'structure'];
  if (seedStages.includes(stage)) return ENANTIODROMIA_IMAGES.seed;
  if (sproutStages.includes(stage)) return ENANTIODROMIA_IMAGES.sprout;
  return ENANTIODROMIA_IMAGES.bloom;
};

const TreeVisual = ({ tree, size = 'large' }) => {
  const getStageEmoji = (stage) => {
    const stages = {
      'seed': '🌱',
      'germination': '🌿',
      'sprout': '🌿',
      'young': '🌲',
      'structure': '🌳',
      'canopy': '🌳',
      'flowering': '🌸',
      'adult': '🌳',
      'ancestral': '🏛️',
      'guardian': '👑'
    };
    return stages[stage] || '🌱';
  };

  const getAuraColor = (auraType) => {
    const colors = {
      'blue': 'from-blue-400/30 to-blue-600/30',
      'green': 'from-green-400/30 to-green-600/30',
      'golden': 'from-yellow-400/30 to-orange-500/30',
      'violet': 'from-purple-400/30 to-violet-600/30',
      'red': 'from-red-400/30 to-rose-600/30',
      'white': 'from-white/40 to-gray-200/40',
    };
    return colors[auraType] || colors.violet;
  };

  const getHealthBg = (health) => {
    const bgs = {
      'flourishing': 'bg-gradient-to-br from-green-100 to-emerald-200',
      'attention': 'bg-gradient-to-br from-yellow-100 to-amber-200',
      'storm': 'bg-gradient-to-br from-orange-100 to-red-200',
      'root_exposed': 'bg-gradient-to-br from-red-100 to-rose-200',
    };
    return bgs[health] || bgs.flourishing;
  };

  if (!tree) return null;

  const sizeClasses = size === 'large' ? 'h-64' : 'h-40';
  const imgSize = size === 'large' ? 'w-48 h-48' : 'w-28 h-28';

  return (
    <div className={`${sizeClasses} ${getHealthBg(tree.state?.health)} rounded-xl flex items-center justify-center relative overflow-hidden`}>
      {/* Aura glow */}
      <div className={`absolute inset-0 bg-gradient-radial ${getAuraColor(tree.aura?.type)} opacity-${Math.min(tree.aura?.intensity || 10, 100)}`} />
      
      {/* Tree image */}
      <div className={`${imgSize} relative z-10`}>
        <img
          src={getTreeImage(tree.state?.stage)}
          alt={tree.species_name || 'Árvore da Enantiodromia'}
          className="w-full h-full object-contain drop-shadow-lg"
          data-testid="nft-tree-image"
        />
      </div>
      
      {/* Scars indicator */}
      {tree.scars?.count > 0 && (
        <div className="absolute top-2 left-2">
          <Badge className="bg-amber-500/80 text-white text-xs">
            {tree.scars.count} cicatriz{tree.scars.count > 1 ? 'es' : ''} de resiliência
          </Badge>
        </div>
      )}
      
      {/* Dormant overlay */}
      {tree.is_dormant && (
        <div className="absolute inset-0 bg-gray-500/50 flex items-center justify-center">
          <Badge className="bg-gray-700 text-white">
            <Moon className="w-4 h-4 mr-1" /> Dormência
          </Badge>
        </div>
      )}
      
      {/* Companion animal */}
      {tree.companion_animal && (
        <div className="absolute bottom-2 right-2 text-3xl">
          {tree.companion_animal === 'owl' && '🦉'}
          {tree.companion_animal === 'wolf' && '🐺'}
          {tree.companion_animal === 'butterfly' && '🦋'}
          {tree.companion_animal === 'phoenix' && '🔥'}
          {tree.companion_animal === 'turtle' && '🐢'}
          {tree.companion_animal === 'eagle' && '🦅'}
        </div>
      )}
    </div>
  );
};

// Componente Heart Score
const HeartScoreDisplay = ({ tree }) => {
  if (!tree) return null;

  const scores = tree.scores || {};
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold flex items-center gap-2">
          <Heart className="h-5 w-5 text-red-500" />
          Heart Score
        </span>
        <span className="text-3xl font-bold text-red-500">{tree.heart_score}</span>
      </div>
      
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="flex items-center gap-1">
              <Shield className="h-4 w-4 text-blue-500" />
              Integridade (35%)
            </span>
            <span>{Math.round((scores.integrity || 0) * 100)}%</span>
          </div>
          <Progress value={(scores.integrity || 0) * 100} className="h-2 bg-blue-100" />
        </div>
        
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="flex items-center gap-1">
              <Droplets className="h-4 w-4 text-green-500" />
              Consistência (25%)
            </span>
            <span>{Math.round((scores.consistency || 0) * 100)}%</span>
          </div>
          <Progress value={(scores.consistency || 0) * 100} className="h-2 bg-green-100" />
        </div>
        
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4 text-purple-500" />
              Comunidade (20%)
            </span>
            <span>{Math.round((scores.community || 0) * 100)}%</span>
          </div>
          <Progress value={(scores.community || 0) * 100} className="h-2 bg-purple-100" />
        </div>
        
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-amber-500" />
              Evolução (20%)
            </span>
            <span>{Math.round((scores.evolution || 0) * 100)}%</span>
          </div>
          <Progress value={(scores.evolution || 0) * 100} className="h-2 bg-amber-100" />
        </div>
      </div>
    </div>
  );
};

export function NFTTreePage() {
  const { user } = useAuth();
  const isPremium = user?.is_premium || user?.is_founder;
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [addictionTypes, setAddictionTypes] = useState([]);
  
  // Modals
  const [showCreateTree, setShowCreateTree] = useState(false);
  const [showRelapse, setShowRelapse] = useState(false);
  const [showCheckup, setShowCheckup] = useState(false);
  
  // Form states
  const [selectedAddiction, setSelectedAddiction] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [customName, setCustomName] = useState('');
  const [relapseNotes, setRelapseNotes] = useState('');
  const [relapseTriggers, setRelapseTriggers] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Carregar dados em paralelo
      const [treeRes, nftsRes, statsRes, typesRes, catalogRes] = await Promise.all([
        fetch(`${API_URL}/api/nft/my-tree`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/nft/my-nfts`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/nft/stats/global`).then(r => r.json()),
        fetch(`${API_URL}/api/nft/addiction-types`).then(r => r.json()),
        fetch(`${API_URL}/api/garden/plants/catalog`).then(r => r.json()),
      ]);
      
      if (treeRes.has_tree) {
        setTree(treeRes.tree);
      }
      setNfts(nftsRes.nfts || []);
      setGlobalStats(statsRes);
      setAddictionTypes(typesRes.addiction_types || []);
      setCatalog(catalogRes);
      
    } catch (error) {
      console.error('Failed to load NFT data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTree = async () => {
    if (!selectedSpecies) {
      toast.error('Selecione uma espécie');
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        species_id: selectedSpecies,
        ...(selectedAddiction && { addiction_type: selectedAddiction }),
        ...(customName && { custom_name: customName }),
      });
      
      const res = await fetch(`${API_URL}/api/nft/create-tree?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        setShowCreateTree(false);
        loadData();
      } else {
        toast.error(data.detail || 'Erro ao criar árvore');
      }
    } catch (error) {
      toast.error('Erro ao criar árvore');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWaterTree = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/nft/water`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        if (data.milestone_achieved) {
          toast.success(`🎉 Milestone alcançado: ${data.milestone_achieved.name}!`, {
            duration: 5000
          });
        }
        loadData();
      } else {
        toast.error(data.detail || 'Erro ao regar');
      }
    } catch (error) {
      toast.error('Erro ao regar árvore');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReportRelapse = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/nft/report-relapse`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: relapseNotes,
          triggers: relapseTriggers,
          seeking_help: true
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message, { duration: 6000 });
        setShowRelapse(false);
        setRelapseNotes('');
        setRelapseTriggers([]);
        loadData();
      } else {
        toast.error(data.detail || 'Erro ao reportar');
      }
    } catch (error) {
      toast.error('Erro ao reportar recaída');
    } finally {
      setActionLoading(false);
    }
  };

  const canWaterToday = () => {
    if (!tree?.last_watered) return true;
    const lastDate = new Date(tree.last_watered).toDateString();
    return lastDate !== new Date().toDateString();
  };

  const getRarityColor = (rarity) => {
    const colors = {
      'common': 'bg-gray-100 text-gray-700',
      'rare': 'bg-blue-100 text-blue-700',
      'epic': 'bg-purple-100 text-purple-700',
      'legendary': 'bg-amber-100 text-amber-700',
      'master': 'bg-gradient-to-r from-amber-400 to-orange-500 text-white',
    };
    return colors[rarity] || colors.common;
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6" data-testid="nft-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-80 rounded-2xl" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
    <div className="max-w-5xl mx-auto space-y-6" data-testid="nft-tree-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <Badge variant="outline" className="text-[10px] rounded-md px-1.5 py-0 h-5 border-emerald-300 text-emerald-700 dark:text-emerald-400 dark:border-emerald-700">Sistema NFT</Badge>
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TreeDeciduous className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
            Árvore Viva
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Sua jornada de recuperação evolui com você
          </p>
        </div>
        
        {!tree && (
          <Button onClick={() => setShowCreateTree(true)} className="rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="create-tree-btn">
            <Sparkles className="mr-2 h-4 w-4" />
            Plantar Minha Semente
          </Button>
        )}
      </div>

      {/* 7-Level Progression Visual */}
      <LevelProgressionBar currentStage={tree?.state?.stage} daysActive={tree?.state?.days_active || 0} />

      {/* Global Stats */}
      {globalStats && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-xl">
                  <TreeDeciduous className="h-5 w-5 text-green-600 dark:text-green-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Árvores Ativas</p>
                  <p className="text-xl font-bold">{globalStats.active_trees}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
                  <Award className="h-5 w-5 text-violet-600 dark:text-violet-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">NFTs Mintados</p>
                  <p className="text-xl font-bold">{globalStats.total_nfts_minted}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
                  <Heart className="h-5 w-5 text-red-500 dark:text-red-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Heart Score</p>
                  <p className="text-xl font-bold">{globalStats.average_heart_score}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/40 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                  <Flame className="h-5 w-5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Recaídas Honestas</p>
                  <p className="text-xl font-bold">{globalStats.total_honest_relapses}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Free user upsell */}
      <PlanBanner isPremium={isPremium} />

      {/* Main Content */}
      {tree ? (
        <Tabs defaultValue="tree" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tree" data-testid="tab-tree">
              <TreeDeciduous className="mr-2 h-4 w-4" />
              Minha Árvore
            </TabsTrigger>
            <TabsTrigger value="nfts" data-testid="tab-nfts">
              <Award className="mr-2 h-4 w-4" />
              Meus NFTs ({nfts.length})
            </TabsTrigger>
            <TabsTrigger value="milestones" data-testid="tab-milestones">
              <Trophy className="mr-2 h-4 w-4" />
              Milestones
            </TabsTrigger>
          </TabsList>

          {/* Tree Tab */}
          <TabsContent value="tree" className="space-y-4">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Tree Visual */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{tree.species_name}</span>
                    <Badge variant="outline">{tree.state?.stage}</Badge>
                  </CardTitle>
                  <CardDescription>
                    Dia {tree.state?.days_active || 0} de jornada • Sequência: {tree.state?.streak_days || 0} dias
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TreeVisual tree={tree} />
                  
                  <div className="mt-4 flex gap-2">
                    <Button 
                      onClick={handleWaterTree}
                      disabled={!canWaterToday() || actionLoading}
                      className="flex-1"
                      data-testid="water-tree-btn"
                    >
                      <Droplets className="mr-2 h-4 w-4" />
                      {canWaterToday() ? 'Regar Árvore' : 'Já regou hoje'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowRelapse(true)}
                      data-testid="relapse-btn"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Vitality */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Vitalidade</span>
                      <span>{tree.state?.vitality || 0}%</span>
                    </div>
                    <Progress value={tree.state?.vitality || 0} className="h-3" />
                  </div>
                </CardContent>
              </Card>

              {/* Heart Score */}
              <Card>
                <CardHeader>
                  <CardTitle>Heart Score</CardTitle>
                  <CardDescription>
                    Sua pontuação baseada nos 4 pilares da recuperação
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <HeartScoreDisplay tree={tree} />
                  
                  {/* Aura */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Aura Atual</span>
                      <Badge className="capitalize">{tree.aura?.type}</Badge>
                    </div>
                    <Progress value={tree.aura?.intensity || 0} className="h-2 mt-2" />
                    <p className="text-xs text-muted-foreground mt-1">
                      Intensidade: {tree.aura?.intensity || 0}%
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* NFTs Tab */}
          <TabsContent value="nfts" className="space-y-4">
            {nfts.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Award className="h-16 w-16 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Nenhum NFT ainda</h3>
                  <p className="text-muted-foreground text-center">
                    Continue regando sua árvore para desbloquear NFTs em cada milestone
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {nfts.map(nft => (
                  <Card key={nft.nft_id} className="overflow-hidden" data-testid={`nft-${nft.nft_id}`}>
                    <div className="h-32 bg-gradient-to-br from-purple-100 to-indigo-200 flex items-center justify-center">
                      <span className="text-5xl">🏆</span>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-sm">{nft.title}</h3>
                        <Badge className={getRarityColor(nft.rarity)}>
                          {nft.rarity}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">{nft.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span>Heart Score: {nft.heart_score}</span>
                        <span>+{nft.mint_value_awa} AWA</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Milestones Tab */}
          <TabsContent value="milestones" className="space-y-4">
            <div className="space-y-3">
              {[
                { day: 0, name: 'A Semente', emoji: '🌱' },
                { day: 7, name: 'Germinação', emoji: '🌿' },
                { day: 30, name: 'Resiliência', emoji: '💪' },
                { day: 60, name: 'Crescimento', emoji: '📈' },
                { day: 90, name: 'Estrutura', emoji: '🏗️' },
                { day: 180, name: 'Expansão', emoji: '🌳' },
                { day: 240, name: 'Florescimento', emoji: '🌸' },
                { day: 365, name: 'Legado', emoji: '👑' },
                { day: 540, name: 'Sabedoria', emoji: '🧠' },
                { day: 720, name: 'Guardião', emoji: '🛡️' },
              ].map(milestone => {
                const achieved = tree.milestones_achieved?.find(m => m.milestone_day === milestone.day);
                const current = tree.state?.days_active || 0;
                const isNext = !achieved && current < milestone.day;
                const progress = Math.min(100, (current / milestone.day) * 100);
                
                return (
                  <Card key={milestone.day} className={achieved ? 'border-green-300 bg-green-50/50' : ''}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`text-3xl ${achieved ? '' : 'grayscale opacity-50'}`}>
                        {milestone.emoji}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">{milestone.name}</h4>
                          <span className="text-sm text-muted-foreground">Dia {milestone.day}</span>
                        </div>
                        {achieved ? (
                          <p className="text-xs text-green-600">
                            ✅ Conquistado em {new Date(achieved.achieved_at).toLocaleDateString('pt-BR')}
                          </p>
                        ) : isNext ? (
                          <div className="mt-1">
                            <Progress value={progress} className="h-1.5" />
                            <p className="text-xs text-muted-foreground mt-1">
                              {milestone.day - current} dias restantes
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            <Lock className="inline h-3 w-3 mr-1" /> Bloqueado
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        /* No Tree - Show Create Flow */
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <TreeDeciduous className="h-24 w-24 text-green-600 mb-6" />
            <h2 className="text-2xl font-bold mb-2">Sua Jornada Começa Aqui</h2>
            <p className="text-muted-foreground text-center max-w-lg mb-6">
              Plante sua Semente Mística e comece a transformar sua recuperação em um ativo digital único. 
              Cada dia de honestidade, cada check-in, cada momento de verdade fortalece sua árvore.
            </p>
            <Button size="lg" onClick={() => setShowCreateTree(true)} className="bg-green-600 hover:bg-green-700">
              <Sparkles className="mr-2 h-5 w-5" />
              Plantar Minha Semente
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Tree Modal */}
      <Dialog open={showCreateTree} onOpenChange={setShowCreateTree}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-green-600" />
              Plantar Sua Semente Mística
            </DialogTitle>
            <DialogDescription>
              Escolha o tipo de jornada e a espécie da sua árvore
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Addiction Type */}
            <div>
              <Label className="text-base font-medium">
                Qual área da sua vida mais precisa de equilíbrio hoje?
              </Label>
              <RadioGroup value={selectedAddiction} onValueChange={setSelectedAddiction} className="mt-3 grid grid-cols-2 gap-3">
                {addictionTypes.map(type => (
                  <div key={type.id}>
                    <RadioGroupItem value={type.id} id={type.id} className="peer sr-only" />
                    <Label
                      htmlFor={type.id}
                      className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-green-500 [&:has([data-state=checked])]:border-green-500 cursor-pointer"
                    >
                      <span className="font-medium">{type.name}</span>
                      <span className="text-xs text-muted-foreground">{type.description}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            {/* Species Selection */}
            <div>
              <Label className="text-base font-medium">Escolha sua espécie</Label>
              {catalog && (
                <div className="mt-3 grid grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                  {Object.values(catalog.by_category || {}).flat().map(plant => (
                    <button
                      key={plant.id}
                      onClick={() => setSelectedSpecies(plant.id)}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        selectedSpecies === plant.id 
                          ? 'border-green-500 bg-green-50 ring-2 ring-green-500' 
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">🌳</div>
                      <div className="text-xs font-medium truncate">{plant.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Custom Name */}
            <div>
              <Label htmlFor="customName">Nome personalizado (opcional)</Label>
              <Input
                id="customName"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Ex: Minha Árvore da Esperança"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTree(false)}>Cancelar</Button>
            <Button 
              onClick={handleCreateTree} 
              disabled={!selectedSpecies || actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading ? 'Plantando...' : '🌱 Plantar Semente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relapse Report Modal */}
      <Dialog open={showRelapse} onOpenChange={setShowRelapse}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Heart className="h-5 w-5" />
              Reportar com Honestidade
            </DialogTitle>
            <DialogDescription>
              <p className="mb-4">
                Sua honestidade é sua maior força. Reportar uma recaída com verdade:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li><strong className="text-green-600">+10%</strong> no seu Score de Integridade</li>
                <li>Cicatriz de Resiliência na sua árvore (aumenta valor NFT)</li>
                <li>Badge "Coragem Brutal" + AWA bônus</li>
                <li>Sua árvore <strong>NÃO</strong> será resetada</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>O que aconteceu? (opcional)</Label>
              <Textarea
                value={relapseNotes}
                onChange={(e) => setRelapseNotes(e.target.value)}
                placeholder="Compartilhe se quiser..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRelapse(false)}>Voltar</Button>
            <Button 
              onClick={handleReportRelapse}
              disabled={actionLoading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {actionLoading ? 'Enviando...' : '💪 Reportar com Coragem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageTransition>
  );
}
